import Main from "niteroi/packages/air-passenger-rights/controllers/Main.controller";
import {SearchField$LiveChangeEvent} from "sap/m/SearchField";
import Table from "sap/m/Table";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import ListBinding from "sap/ui/model/ListBinding";
import {Route$PatternMatchedEvent} from "sap/ui/core/routing/Route";
import {ListItemBase$PressEvent} from "sap/m/ListItemBase";
import {Button$PressEvent} from "sap/m/Button";
import Toolbar from "sap/m/Toolbar";
import SimpleForm from "sap/ui/layout/form/SimpleForm";
import Page from "sap/m/Page";
import Ui5File from "sap/ui/core/util/File";
import {ILoadDataResponse} from "niteroi/packages/system/models/interfaces";

export default class Flights extends Main {
    private readonly onPatternMatchedHandler = this.onPatternMatched.bind(this);

    public onInit(): void {
        super.onInit();
        const router = this.getRouter();
        const routeName = this.getCurrentRouteName(router);
        if (routeName) {
            const route = router.getRoute(routeName);

            if (route.getPattern().includes("flights/add")) {
                this.resetOriginalModel("flight");
            } else {
                route.detachPatternMatched(this.onPatternMatchedHandler);
                route.attachPatternMatched(this.onPatternMatchedHandler);
            }
        }
    }

    public onBeforeRendering(): void {
        this.setActiveTileByRoute();
    }

    protected onPatternMatched(event: Route$PatternMatchedEvent): void {
        this.resetOriginalModel("flight");
        const params = event.getParameters();
        const id = (params.arguments as { id: string }).id ?? "";
        this.getData(id);
    }

    protected getData(objectId: string = ""): void {
        const url = objectId ? `/api/air-passenger-rights/flights/${objectId}` : "/api/air-passenger-rights/flights";
        const modelName = objectId ? "flight" : "flights";
        this.fetchData(url, modelName, objectId);
    }

    /**
     * Search handler for flights table
     * Filters flights by origin, destination, flight number, and airline name
     * @param event - The search event containing the search query
     */
    public onListSearch(event: SearchField$LiveChangeEvent): void {
        const searchQuery = event.getParameter("newValue");
        const flightsTable = this.byId("idFlightsTable") as Table;

        if (!flightsTable) {
            return;
        }

        const binding = flightsTable.getBinding("items") as ListBinding;
        if (!binding) {
            return;
        }

        // If search query is empty, reset to default filters
        if (!searchQuery || searchQuery.trim() === "") {
            binding.filter([]);
            return;
        }

        // Create filters for each searchable field with case-insensitive Contains
        const searchFilters: Filter[] = [
            new Filter("origin", FilterOperator.Contains, searchQuery),
            new Filter("destination", FilterOperator.Contains, searchQuery),
            new Filter("flightNumber", FilterOperator.Contains, searchQuery),
            new Filter("iataCode", FilterOperator.Contains, searchQuery)
        ];

        const customFilter = new Filter({
            path: "",
            test: (value: { iataCode: string, flightNumber: string }): boolean => {
                if (!value) {
                    return false;
                }
                const normalizedQuery = searchQuery.toLowerCase().replace(/\s+/g, "");

                const iata = (value.iataCode || "").toString().toLowerCase();
                const flightNum = (value.flightNumber || "").toString().toLowerCase();
                const fullFlightCode = iata + flightNum;

                return (
                    fullFlightCode.includes(normalizedQuery) ||
                    iata.includes(normalizedQuery) ||
                    flightNum.includes(normalizedQuery)
                );
            }
        });

        // Combine all search filters with OR logic
        const combinedSearchFilter = new Filter({
            filters: [...searchFilters, customFilter],
            and: false // OR condition - match any field
        });

        // Keep the base filters for valid codes (AND with search)
        const baseFilters = [
            new Filter("origin", FilterOperator.NE, null),
            new Filter("destination", FilterOperator.NE, null)
        ];

        // Combine base filters with search filter using AND logic
        const finalFilters = [
            ...baseFilters,
            combinedSearchFilter
        ];

        binding.filter(finalFilters);
    }

    public onPressColumnListItemDetail(event: ListItemBase$PressEvent): void {
        this.resetOriginalModel("flight");
        const source = event.getSource();
        const bindingContext = source.getBindingContext("flights");
        const selectedListItem = bindingContext.getModel().getProperty(bindingContext.getPath());
        this.navTo("air-passenger-rights/flights/edit", {id: selectedListItem.flightId}, false);
    }

    public onPressButtonDiscardFormEdit(): void {
        this.discardChanges("flight");
    }

    public onPressMenuItemAdd(): void {
        this.resetOriginalModel("flight");
        this.navTo("air-passenger-rights/flights/add", {}, false);
    }

    public onPressButtonSaveFormEdit(event: Button$PressEvent): void {
        const source = event.getSource();
        const page = ((source.getParent() as Toolbar).getParent() as SimpleForm).getParent() as Page;
        page.setBusy(true);
        const model = this.getModel("flight");
        if (JSON.stringify(this.originalData) !== model.getJSON()) {
            const flightId = model.getProperty("/flightId") as string;
            const method = flightId ? "PUT" : "POST";
            const requestModel = this.createRequestModel();
            const url = flightId ? `/api/air-passenger-rights/flights/${flightId}` : "/api/air-passenger-rights/flights";
            const headers = this.getRequestHeaders();
            requestModel.loadData(url, model.getJSON(), true, method, false, true, headers).then(() => {
                model.setData(JSON.parse(requestModel.getJSON()));
                this.showToast(this.getText("msgFlightSavedSuccess"));
            }).catch((response: ILoadDataResponse) => {
                const responseText = JSON.parse(response.responseText);
                this.showToast(`${responseText.code} - ${responseText.error}`);
            }).finally(() => {
                page.setBusy(false);
            });
        } else {
            page.setBusy(false);
        }
    }

    public onPressMenuItemExportJson(): void {
        this.exportToJSON("flights");
    }

    public onPressDownloadTemplateBtn(): void {
        // Template based on flights structure
        const template = [{
            "iataCode": "TP",
            "flightNumber": "1032",
            "departureAt": "05/11/2022 21:20",
            "arrivalAt": "05/11/2022 23:30",
            "origin": "LIS",
            "destination": "BCN"
        }];
        Ui5File.save(JSON.stringify(template, null, 4), "flights_template", "json", "application/json", "utf-8");
    }
}
